/* Hallmark · component: TagInput · genre: modern-minimal · theme: maeiz-tokens
 * Inline search-style field that creates removable tags (chips).
 */
import React, { useCallback, useRef, useState } from "react";
import { clsx } from "clsx";
import { Search, X } from "lucide-react";
import { fieldSurfaceClass } from "../../styles/field";

export interface TagInputProps {
    value: string[];
    onChange: (tags: string[]) => void;
    label?: string;
    placeholder?: string;
    suggestions?: string[];
    disabled?: boolean;
    error?: string;
    fullWidth?: boolean;
    className?: string;
    id?: string;
}

export const TagInput: React.FC<TagInputProps> = ({
    value,
    onChange,
    label,
    placeholder = "Type and press Enter…",
    suggestions = [],
    disabled,
    error,
    fullWidth = true,
    className,
    id,
}) => {
    const [draft, setDraft] = useState("");
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const fieldId = id ?? "tag-input";

    const filtered = suggestions.filter(
        (s) => s.toLowerCase().includes(draft.trim().toLowerCase()) && !value.includes(s),
    );

    const add = useCallback(
        (raw: string) => {
            const tag = raw.trim();
            if (!tag || value.includes(tag)) return;
            onChange([...value, tag]);
            setDraft("");
            setOpen(false);
        },
        [onChange, value],
    );

    const remove = (tag: string) => onChange(value.filter((t) => t !== tag));

    return (
        <div className={clsx("flex flex-col gap-1.5", fullWidth && "w-full", className)}>
            {label ? (
                <label htmlFor={fieldId} className="text-sm font-medium text-neutral-dark dark:text-white">
                    {label}
                </label>
            ) : null}
            <div
                className={clsx(
                    fieldSurfaceClass({ error: !!error, disabled }),
                    "relative flex min-h-11 flex-wrap items-center gap-1.5 px-2.5 py-1.5",
                )}
                onClick={() => inputRef.current?.focus()}
            >
                <Search size={16} className="ms-1 shrink-0 text-primary dark:text-gold-light" aria-hidden />
                {value.map((tag) => (
                    <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary dark:bg-gold/15 dark:text-gold-light"
                    >
                        {tag}
                        {!disabled ? (
                            <button
                                type="button"
                                className="rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/10"
                                aria-label={`Remove ${tag}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    remove(tag);
                                }}
                            >
                                <X size={12} />
                            </button>
                        ) : null}
                    </span>
                ))}
                <input
                    id={fieldId}
                    ref={inputRef}
                    value={draft}
                    disabled={disabled}
                    placeholder={value.length === 0 ? placeholder : ""}
                    className="min-w-[8rem] flex-1 bg-transparent py-1 text-sm outline-none placeholder:text-neutral-secondary dark:text-white"
                    onChange={(e) => {
                        setDraft(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => window.setTimeout(() => setOpen(false), 120)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            add(draft);
                        } else if (e.key === "Backspace" && !draft && value.length) {
                            remove(value[value.length - 1]);
                        }
                    }}
                />
                {open && draft && filtered.length > 0 ? (
                    <ul className="absolute start-0 end-0 top-[calc(100%+4px)] z-[80] max-h-48 overflow-y-auto rounded-xl border border-neutral-border bg-white p-1 shadow-mz-lg dark:border-white/12 dark:bg-neutral-navy">
                        {filtered.slice(0, 8).map((s) => (
                            <li key={s}>
                                <button
                                    type="button"
                                    className="w-full rounded-lg px-2.5 py-2 text-start text-sm hover:bg-neutral-light dark:hover:bg-white/10"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => add(s)}
                                >
                                    {s}
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>
            {error ? (
                <p className="text-sm text-danger" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};
